import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function ProviderWithdrawalForm({ available, slaMessage, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [upiId, setUpiId] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/providers/me').then(({ data }) => {
      const p = data.provider || {};
      setMethod(p.withdrawal_method || (p.bank_account_number ? 'bank_transfer' : 'upi'));
      setUpiId(p.withdrawal_upi_id || '');
      setAccountHolder(p.bank_account_holder || '');
      setAccountNumber(p.bank_account_number || '');
      setBankName(p.bank_name || '');
      setIfsc(p.bank_ifsc || '');
    }).catch(() => {});
  }, []);

  const maxAvailable = Math.max(0, Number(available) || 0);

  const submit = async () => {
    const val = Number(amount);
    if (val <= 0) {
      toast.error('Amount must be positive');
      return;
    }
    if (val > maxAvailable) {
      toast.error('Amount exceeds available balance');
      return;
    }
    setLoading(true);
    try {
      await api.patch('/providers/me/withdrawal-details', {
        method,
        upi_id: method === 'upi' ? upiId : undefined,
        account_holder: method === 'bank_transfer' ? accountHolder : undefined,
        account_number: method === 'bank_transfer' ? accountNumber : undefined,
        bank_name: method === 'bank_transfer' ? bankName : undefined,
        ifsc: method === 'bank_transfer' ? ifsc : undefined,
      });
      await api.post('/providers/me/withdrawals', { amount: val });
      toast.success(slaMessage || 'Your payment will be credited to your account within 48 hours.');
      setOpen(false);
      setAmount('');
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button className="w-full mt-4" data-testid="withdraw-earnings-btn" onClick={() => setOpen(true)} disabled={maxAvailable <= 0}>
        Withdraw Earnings
      </Button>
    );
  }

  return (
    <div className="resqly-card p-4 mt-4 space-y-3" data-testid="withdrawal-form">
      <div className="font-semibold">Withdraw Earnings</div>
      <p className="text-xs text-slate-500">Available: ₹{maxAvailable.toFixed(2)}</p>
      <input
        type="number"
        className="w-full border rounded p-2"
        placeholder="Amount"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid="withdrawal-amount"
      />
      <div className="flex gap-4 text-sm">
        <label><input type="radio" checked={method === 'upi'} onChange={() => setMethod('upi')} /> UPI</label>
        <label><input type="radio" checked={method === 'bank_transfer'} onChange={() => setMethod('bank_transfer')} /> Bank</label>
      </div>
      {method === 'upi' ? (
        <input className="w-full border rounded p-2" placeholder="UPI ID" value={upiId} onChange={(e) => setUpiId(e.target.value)} />
      ) : (
        <>
          <input className="w-full border rounded p-2" placeholder="Account holder" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} />
          <input className="w-full border rounded p-2" placeholder="Account number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
          <input className="w-full border rounded p-2" placeholder="Bank name" value={bankName} onChange={(e) => setBankName(e.target.value)} />
          <input className="w-full border rounded p-2" placeholder="IFSC" value={ifsc} onChange={(e) => setIfsc(e.target.value)} />
        </>
      )}
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
        <Button className="flex-1" disabled={loading} data-testid="confirm-withdrawal" onClick={submit}>Submit</Button>
      </div>
    </div>
  );
}
